import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { usePortal } from '../PortalContext';
import { usePortalJobs } from '../hooks/usePortalJobs';
import { usePortalEstimates } from '../hooks/usePortalEstimates';
import { usePortalInvoices } from '../hooks/usePortalInvoices';
import { invoiceBalance, invoiceIsPayable, type PortalEstimateRow, type PortalInvoiceRow, type PortalJobRow } from '../hooks/portalClient';
import { estimateStatus, invoiceStatus, jobIsOpen, jobStatus } from '../status';
import { PortalSection } from '../components/PortalSection';
import { PortalListRow } from '../components/PortalListRow';
import { PortalLoading } from '../components/PortalLoading';
import { PortalErrorState } from '../components/PortalErrorState';

const JOBS_PREVIEW = 5;

export default function PortalHome() {
  const { slug, company, me, timezone, formatMoney, formatShortDate } = usePortal();
  const jobsQ = usePortalJobs(company.id);
  const estimatesQ = usePortalEstimates(company.id);
  const invoicesQ = usePortalInvoices(company.id);
  const [showAllJobs, setShowAllJobs] = useState(false);

  if (jobsQ.isLoading || estimatesQ.isLoading || invoicesQ.isLoading) {
    return <PortalLoading label="Loading your account…" />;
  }
  if (jobsQ.error || estimatesQ.error || invoicesQ.error) {
    return (
      <PortalErrorState
        onRetry={() => { void jobsQ.refetch(); void estimatesQ.refetch(); void invoicesQ.refetch(); }}
      />
    );
  }

  const estimates = estimatesQ.data ?? [];
  const invoices = invoicesQ.data ?? [];
  const jobs = sortJobs(jobsQ.data ?? []);

  const attentionEstimates = estimates.filter((e) => e.can_respond);
  const attentionInvoices = invoices.filter(invoiceIsPayable);
  const visibleJobs = showAllJobs ? jobs : jobs.slice(0, JOBS_PREVIEW);

  const estimateRow = (e: PortalEstimateRow) => (
    <PortalListRow
      key={e.id}
      to={`/portal/${slug}/estimates/${e.id}`}
      title={`Quote ${e.estimate_number}`}
      date={formatShortDate(e.sent_at ?? e.created_at)}
      description={[formatMoney(e.estimate_amount), e.service_description?.trim()].filter(Boolean).join(' — ')}
      status={estimateStatus(e, timezone)}
    />
  );

  const invoiceRow = (i: PortalInvoiceRow) => {
    const balance = invoiceBalance(i);
    const total = formatMoney(i.total_amount);
    return (
      <PortalListRow
        key={i.id}
        to={`/portal/${slug}/invoices/${i.id}`}
        title={`Invoice ${i.invoice_number}`}
        date={formatShortDate(i.issue_date ?? i.created_at)}
        description={balance > 0 && i.status !== 'cancelled' ? `${formatMoney(balance)} outstanding of ${total}` : total}
        status={invoiceStatus(i, formatShortDate)}
      />
    );
  };

  const jobRow = (j: PortalJobRow) => (
    <PortalListRow
      key={j.id}
      to={`/portal/${slug}/jobs/${j.id}`}
      title={`Job ${j.job_number}`}
      date={formatShortDate(j.scheduled_date ?? j.job_date ?? j.created_at)}
      description={[j.service_type_name, j.issue_description?.trim()].filter(Boolean).join(' — ')}
      status={jobStatus(j.status)}
    />
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Hello {firstName(me.customer_name)}</h1>
        <p className="text-sm text-muted-foreground">Your quotes, invoices and jobs with {company.trading_name || company.name}.</p>
      </div>

      {(attentionEstimates.length > 0 || attentionInvoices.length > 0) && (
        <PortalSection title="Needs your attention">
          {attentionEstimates.map(estimateRow)}
          {attentionInvoices.map(invoiceRow)}
        </PortalSection>
      )}

      <PortalSection title="Quotes" empty="No quotes yet.">
        {estimates.map(estimateRow)}
      </PortalSection>

      <PortalSection title="Invoices" empty="No invoices yet.">
        {invoices.map(invoiceRow)}
      </PortalSection>

      <PortalSection
        title="Jobs"
        empty="No jobs yet."
        action={
          jobs.length > JOBS_PREVIEW ? (
            <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setShowAllJobs((v) => !v)}>
              {showAllJobs ? 'Show fewer' : `Show all (${jobs.length})`}
            </Button>
          ) : undefined
        }
      >
        {visibleJobs.map(jobRow)}
      </PortalSection>

      <Card>
        <CardContent className="flex flex-col items-start gap-3 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-medium">Need something?</p>
            <p className="text-sm text-muted-foreground">Send a short message and the office will get back to you.</p>
          </div>
          <Button asChild variant="outline" className="gap-1.5">
            <Link to={`/portal/${slug}/message`}>
              <MessageSquare className="h-4 w-4" /> Send us a message
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function firstName(name: string | null | undefined): string {
  return (name ?? '').trim().split(/\s+/)[0] || 'there';
}

/** Upcoming (open) jobs soonest-first, then past jobs most-recent-first. */
function sortJobs(jobs: PortalJobRow[]): PortalJobRow[] {
  const when = (j: PortalJobRow) => j.scheduled_date ?? j.job_date ?? j.created_at.slice(0, 10);
  const open = jobs.filter((j) => jobIsOpen(j.status)).sort((a, b) => when(a).localeCompare(when(b)));
  const past = jobs.filter((j) => !jobIsOpen(j.status)).sort((a, b) => when(b).localeCompare(when(a)));
  return [...open, ...past];
}
